import React, { useCallback, useMemo } from 'react';

import { is3pProductKeyForAnalytics } from '../../../../common/constants/products';
import { type HistoryItemData } from '../../../../common/types';
import { EventTypeMessage, VIEWED_EVENT } from '../../../../common/ui/event-type-message';
import { ProductIcon } from '../../../../common/ui/product-icon';
import {
	SearchResult,
	type SearchResultEventArguments,
} from '../../../../common/ui/quick-find/search-dialog-result';
import { type State } from '../../../../common/utils/quick-find/state';
import { getResultConnectorSource } from '../../../../common/utils/third-party-utils';
import { type QuickFindResultEvent } from '../../../../controllers/quick-find/utils';
import { useThirdPartyConfigs } from '../../../../controllers/store/bootstrap';

export type BrowserHistoryItem = {
	id: string;
	type: 'browser-history';
	data: HistoryItemData;
};

type BrowserHistoryProps = {
	result: BrowserHistoryItem;
	state: State;
	onClick?: (props: QuickFindResultEvent) => void;
	onContextMenu?: (props: QuickFindResultEvent) => void;
	onHighlighted?: (props: QuickFindResultEvent) => void;
};

export const BrowserHistory = ({
	result,
	state,
	onClick,
	onContextMenu,
	onHighlighted,
}: BrowserHistoryProps): React.JSX.Element => {
	const { data } = result;
	const is3pBrowserHistory = is3pProductKeyForAnalytics(data.productKey);
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const icon = <ProductIcon size="small" product={data.productKey} />;

	const rightMetadata = (
		<EventTypeMessage
			timestamp={new Date(data.lastVisitTime).toISOString()}
			eventType={VIEWED_EVENT.VIEWED}
			isMyActivityData={true}
		/>
	);

	const additionalAnalyticsAttributes = useMemo(
		() => ({
			metadata: {
				product: data.productKey,
				connectorType: getResultConnectorSource({
					resultNodeId: result.id,
					thirdPartyConfigs,
					productKey: result.data.productKey,
				}),
			},
		}),
		[data.productKey, result.id, thirdPartyConfigs, result.data.productKey],
	);

	const onResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			searchData: SearchResultEventArguments,
		) => {
			onClick?.({
				event,
				data: searchData,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onClick, state],
	);

	const onResultContextMenu = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			searchData: SearchResultEventArguments,
		) => {
			onContextMenu?.({
				event,
				data: searchData,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onContextMenu, state],
	);

	const onResultHighlighted = useCallback(
		(searchData: SearchResultEventArguments) => {
			onHighlighted?.({
				data: searchData,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onHighlighted, state],
	);

	return (
		<SearchResult
			id={result.id}
			icon={icon}
			title={data.title}
			resultType="historyActivities"
			product={data.productKey}
			url={data.url}
			rightMetadata={rightMetadata}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onResultHighlighted={onResultHighlighted}
			isThirdParty={is3pBrowserHistory}
		/>
	);
};
